// appTheme.js
import { createTheme } from "@mui/material";
import { Paper } from "@mui/material";
// Landing palette — used here ONLY for the landing-specific hero typography
// variants (heroTitle / h0 / intro). All signed-in app styling comes from tokens.
import { COLORS } from "./landingTheme";
import {
  ACCENT,
  ACCENT_DARK,
  ACCENT_TINT,
  CANVAS,
  SURFACE,
  SURFACE_MUTED,
  HAIRLINE,
  HAIRLINE_STRONG,
  INK,
  INK_SECONDARY,
  INK_MUTED,
  HOVER,
  SHADOW_SM,
  SHADOW_MD,
  SHADOW_LG,
  RADIUS,
  FONT,
  STATUS,
} from "./tokens";

/**
 * Application theme configuration
 * Defines the Material-UI theme for the entire application
 */
export const appTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: ACCENT, dark: ACCENT_DARK, contrastText: "#ffffff" },
    secondary: { main: ACCENT },
    // Semantic colors — reserved for status meaning (fixes the old
    // success/brand collision where success was the brand lime).
    success: { main: STATUS.success, contrastText: "#ffffff" },
    warning: { main: STATUS.warning, contrastText: "#ffffff" },
    error: { main: STATUS.error, contrastText: "#ffffff" },
    info: { main: STATUS.info, contrastText: "#ffffff" },
    background: {
      default: CANVAS, // app canvas (subtle neutral)
      paper: SURFACE, // card / surface (white)
    },
    text: {
      primary: INK, // primary text (slate-900)
      secondary: INK_SECONDARY, // secondary text (slate-600)
      hint: INK_SECONDARY,
    },
    border: {
      default: HAIRLINE,
    },
    divider: HAIRLINE,
  },
  components: {
    MuiMenuItem: {
      // Menu/Select items: align to the body type scale and ink color
      // (was an off-scale 1rem in pure black).
      styleOverrides: {
        root: { fontSize: FONT.md, color: INK },
      },
    },
    MuiTableContainer: {
      defaultProps: {
        component: Paper, // default render component: Paper
      },
      styleOverrides: {
        root: {
          border: `1px solid ${HAIRLINE}`,
          borderRadius: RADIUS.md, // unified card radius (12px)
          backgroundColor: SURFACE,
          marginBottom: "24px", // mb: 4
          overflow: "hidden",
          boxShadow: SHADOW_SM,
          transition: "box-shadow 0.2s ease",

          "&:hover": {
            boxShadow: SHADOW_MD,
          },

          // prevent nested Paper from adding extra border radius
          "& .MuiPaper-root": {
            borderRadius: 0,
          },
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          "& .MuiInputBase-input": {
            fontSize: FONT.md,
            color: INK, // primary input text color
          },
          "& .MuiFormHelperText-root": {
            fontSize: FONT.md,
            color: INK_SECONDARY, // helper text color (gray)
          },
        },
      },
    },

    /* Label styles (TextField label) */
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: FONT.md,
          color: INK_SECONDARY, // default hint color (gray)
          "&.Mui-focused": {
            color: ACCENT, // accent color when focused
          },
          "&.Mui-disabled": {
            color: INK_MUTED, // disabled state lighter gray
          },
          "&.Mui-error": {
            color: STATUS.error, // error state red
          },
          "&.MuiInputLabel-shrink": {
            color: ACCENT, // label color when shrunk (floating)
          },
        },
        shrink: {
          transform: "translate(14px, -9px) scale(0.85)",
          transformOrigin: "top left",
        },
      },
    },

    /* Fallback styles for input text */
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          fontSize: FONT.md,
          color: INK,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: SURFACE,
          minHeight: "100vh",
          margin: 0,
          padding: 0,
          overflowX: "hidden",
          color: INK,
        },
        "*": { boxSizing: "border-box", margin: 0, padding: 0 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: RADIUS.sm,
          padding: "8px 16px",
          transition: "all 0.2s ease-in-out",
        },
      },

      variants: [
        {
          props: { variant: "solidButton" },
          style: ({ theme }) => ({
            // reuse contained variant styles first
            ...theme.components?.MuiButton?.variants
              ?.find((v) => v.props?.variant === "contained")
              ?.style?.({ theme }),

            // then apply the accent background
            background: ACCENT,
            boxShadow: "none",
            textTransform: "none",
            color: "#ffffffff",
            "&:hover": {
              background: ACCENT_DARK,
              color: "#ffffffff",
              boxShadow: "none",
            },

            "&.Mui-disabled": {
              backgroundColor: "rgba(0,0,0,0.1)", // light gray background
              color: "rgba(0,0,0,0.4)", // gray text
              boxShadow: "none",
            },
          }),
        },
        {
          props: { variant: "outlinedButton" },
          style: () => {
            const c = ACCENT;
            const hc = ACCENT_DARK;
            return {
              // Baseline: outlined visual
              backgroundColor: "transparent",
              color: c,
              border: `1.5px solid ${c}`,
              textTransform: "none",
              boxShadow: "none",

              // Interaction states
              "&:hover": {
                backgroundColor: ACCENT_TINT,
                color: hc,
                borderColor: hc,
                boxShadow: "none",
              },
              "&:active": { transform: "translateY(0)" },
              "&.Mui-disabled": {
                opacity: 0.5,
                color: c,
                borderColor: c,
              },
            };
          },
        },
        {
          props: { variant: "subtleButton" },
          style: {
            textTransform: "none",
            fontSize: "1.05rem",
            fontWeight: 500,
            color: "#111827",
            backgroundColor: "#f9fafb",
            border: "1px solid #d1d5db",
            borderRadius: "999px",
            padding: "4px 14px",
            transition: "all 0.18s ease-out",
            boxShadow: "0 0 0 rgba(15, 23, 42, 0)",
            cursor: "pointer",

            "&:hover": {
              backgroundColor: "#ffffff",
              borderColor: "#9ca3af",
              boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: "0 0 0 rgba(15, 23, 42, 0)",
            },
          },
        },
        {
          props: { variant: "glass" },
          style: {
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(8px)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.3)",
            "&:hover": {
              background: "rgba(255,255,255,0.2)",
            },
          },
        },
        {
          props: { variant: "textGradient" },
          style: {
            background: "transparent",
            backgroundImage:
              "linear-gradient(90deg, #3F9A82, #427676, #3B596A)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: 700,
          },
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
          borderRadius: RADIUS.md,
        },
      },
      variants: [
        {
          props: { variant: "defaultCard" },
          style: {
            background: SURFACE,
            border: `1px solid ${HAIRLINE}`,
            boxShadow: SHADOW_SM,
            padding: "24px",
            transition: "box-shadow 0.2s ease, border-color 0.2s ease",
            "&:hover": { boxShadow: SHADOW_SM },
          },
        },
        {
          props: { variant: "defaultCardNoShadow" },
          style: {
            background: SURFACE,
            border: `1px solid ${HAIRLINE}`,
            padding: "24px",
            transition: "box-shadow 0.2s ease, border-color 0.2s ease",
          },
        },
        {
          // Used to highlight assigned/primary cards. Now a calm, elevated
          // surface that lifts subtly on hover (no rainbow glow).
          props: { variant: "whiteCardColorfulShadow" },
          style: {
            width: "100%",
            background: SURFACE,
            border: `1px solid ${HAIRLINE}`,
            boxShadow: SHADOW_MD,
            position: "relative",
            overflow: "visible",
            transition:
              "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: SHADOW_LG,
              borderColor: HAIRLINE_STRONG,
            },
          },
        },
      ],
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: SURFACE,
          color: INK,
          boxShadow: "none",
          borderBottom: `1px solid ${HAIRLINE}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            backgroundColor: SURFACE_MUTED,
            color: INK_SECONDARY,
            fontWeight: 600,
            fontSize: "0.8125rem",
            letterSpacing: "0.01em",
            borderBottom: `1px solid ${HAIRLINE}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${HAIRLINE}`,
          color: INK,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { "&:hover": { backgroundColor: HOVER } },
      },
    },
    MuiChip: {
      styleOverrides: {},
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: HAIRLINE },
      },
    },
  },
  typography: {
    fontFamily: ["Inter", "Helvetica", "Arial", "sans-serif"].join(","),

    // The biggest h0 style for HeroSection
    // Gradient text effect
    heroTitle: {
      fontWeight: 800,
      lineHeight: 1,
      background: COLORS.GRADIENTS.MAIN(),
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      fontSize: "4rem",
    },

    iconHeader: {
      fontWeight: 800,
      lineHeight: 1,
      background: COLORS.TEXT.PRIMARY,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      fontSize: "1.25rem",
    },

    h0: {
      fontWeight: 800,
      lineHeight: 3,
      background: COLORS.GRADIENTS.MAIN(),
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      fontSize: "3rem",
    },

    h1: {
      fontSize: "3.5rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: INK,
    },
    h2: {
      fontSize: "2.5rem",
      fontWeight: 600,
      letterSpacing: "-0.01em",
      color: INK,
    },
    h3: { fontSize: "2rem", fontWeight: 600, color: INK },

    // Page & section titles inside the signed-in app — sized for a data UI,
    // hierarchy via size/weight rather than heavy bold.
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      letterSpacing: "-0.01em",
      color: INK,
    },
    h5: { fontSize: "1.25rem", fontWeight: 600, color: INK },
    h6: { fontSize: "1.05rem", fontWeight: 600, color: INK },

    intro: {
      color: COLORS.TEXT.SECONDARY,
      fontSize: "1.25rem",
      lineHeight: 1.85,
      maxWidth: 720,
      textAlign: { xs: "center", md: "left" },
    },

    body1: { fontSize: "0.875rem", lineHeight: 1.6, color: INK },
    body2: { fontSize: "0.8125rem", lineHeight: 1.55, color: INK_SECONDARY },
  },
  shape: { borderRadius: RADIUS.sm },
});
